import React, { useState, useEffect } from "react";
import {Link} from "react-router-dom";
import {
    renderRatingStars,
    formatDate,
    createElementFromHTML,
    valueExists
} from '../../utils/utilsHelpers';
import {JSON_HEADERS} from '../../constants/constants';
import {SENTIMENT_SCORE_TOOLTIP } from '../../constants/reviewsConstants.js';
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Image from 'react-bootstrap/Image';
import Collapse from 'react-bootstrap/Collapse';
import TooltipTrigger from "../bootstrap/TooltipTrigger";
import ConditionalWrapper from "../../utils/ConditionalWrapper";
import UserCard from '../users/UserCard';
import PostingActions from "../postings/PostingActions";
import CommentCard from "../comments/CommentCard";
import CommentForm from "../comments/CommentForm";
import { captureImpression } from 'javascripts/impressions/capture_impression.js';
import PostingAdminMenu from "../postings/PostingAdminMenu";
import getVideoId from 'get-video-id';
import { isSafari, isFirefox } from "react-device-detect";

export default function ReviewCard(props) {

    let {
        review,
        sections,
        showProduct=true,
        showDate=true,
        showRating=true,
        showUserBadges=false,
        showLikes=false,
        showTitle=true,
        showSnippet=false,
        displaySnippetAsQuote=false,
        showBody=false,
        collapsedBody=true,
        showProsCons=false,
        showComments=false,
        showNewCommentForm=false,
        showReviewActions=false,
        showProductVersion=false,
        showAudio=false,
        playAudio=()=>{},
        selectedAudio={},
        avatarSize=60,
        titleClamp=0,
        titleFont=20,
        logoSize=60,
        currentUser,
        token,
        displayInCard=true,
        reviewCtaText='Read full review',
        authorCardClasses='',
        commentCardClasses='',
        urlAsRoute=false,
        showUser=true,
        gtmEventRegion='',
        showProductUserVerification=false,
        showSentimentScore=false,
        showShortSummary=false,
        toggleRegistration=()=>{},
        modalOverlay=false,
        tooltips,
        leadData={},
        includeForeignLanguageLink=false
    } = props;

    const [showCommentForm, setShowCommentForm] = useState(false);
    const [comments, setComments] = useState(review.comments);
    const [showMoreComments, setShowMoreComments] = useState(review.comment_count > 2);
    const [commentCount, setCommentCount] = useState(review.comment_count);
    const [isPlaying, setIsPlaying] = useState(false);
    const [hideAudio, setHideAudio] = useState(false);

    const userSignedIn = !!currentUser;
    const viewingSelf = currentUser?.id === review.author?.id;
    const adminUser = !!currentUser?.admin;

    useEffect(() => {
        // reset when the review is updated from the parent
        setComments(review.comments);
        setShowMoreComments(review.comment_count > 2);
        setCommentCount(review.comment_count);
        setShowCommentForm(false);
        setIsPlaying(false);
    }, [review]);

    useEffect(() => {
        if(showAudio) {
            setHideAudio(!!Object.keys(selectedAudio).length); // hide audio element when audio component renders in the AudioModal
        }
    }, [selectedAudio]);

    // event handlers

    const onToggleCommentForm = (show) => {
        setShowCommentForm(show);
    }

    const onDeleteComment = (commentId) => {
        let currComments = [...comments];
        let newComments = currComments.filter((c) => c.id != commentId);
        let newCommentCount = commentCount - 1;
        // setComments(newComments);
        setCommentCount(newCommentCount);
        props.onCommentsChangeHandler(review.id, newComments, newCommentCount);
    }

    const onNewComment = (comment) => {
        let newComments = [comment, ...comments];
        // setComments(newComments);
        setShowCommentForm(false);
        let newCommentCount = commentCount + 1;
        setCommentCount(newCommentCount);
        props.onCommentsChangeHandler(review.id, newComments, newCommentCount);
    }

    const onRepliesChangeHandler = (comment) => {
        let newComments = comments.map(c => {
            return (c.id === comment.id) ? comment : c;
        });
        props.onCommentsChangeHandler(review.id, newComments, commentCount);
    }

    const onReadFullReview = async(review) => {
        captureImpression(review.actions.url);
        props.onReadReview(review);
    }

    const onListComments = async() => {
        await fetch(`/comments/list?posting_id=${review.id}`, {
            method: 'GET',
            headers: JSON_HEADERS
        }).then(function(response) {
            return response.json();
        }).then(function(jsonResponse) {
            setComments(jsonResponse.comments);
            setShowMoreComments(false);
        });
    }

    const onAudioListen = () => {
        // may need to set this up with LeadGen, if we need phone verification, etc...
        if(currentUser) {
            playAudio(review.actions.audio.url, review.actions.audio.type)
        } else {
            toggleRegistration({
                leadCode: 'AUDIO_HIGHLIGHT',
                cta: 'Register',
                modalOverlay: modalOverlay,
                gtmEventRegion: props.gtmEventRegion,
                gtmEventElement: 'listen',
                withConfirmationScreen: false,
                actionParams: { selected: { url: review.actions.audio.url, type: review.actions.audio.type }} ,
                hiddenTags: {
                    ...review.lead_gen_attributes, 'todo': 'generic_lead', itemid: review.id, 'lead[lead_code]': 'AUDIO_HIGHLIGHT' }
            });
        }
    }

    const onForeignLanguageClick = (event) => {
        event.preventDefault();

        const modalBody = document.querySelector("#review-modal");
        const targetElement = document.getElementById("foreign-language");

        if (modalBody) {
            // review in modal
            modalBody.scrollTo({
                top: targetElement.offsetTop + 40,
                behavior: "smooth"
            });
        } else {
            // review in peer-research
            window.scrollTo({
                top: targetElement.offsetTop + 60,
                behavior: "smooth"
            });
        }
    }

    // render methods

    const renderProduct = () => {
        if(showProduct && review.product) {
            return (
              <div className={'product'}>
                  <Image
                    thumbnail
                    src={review.product.thumbnail}
                    alt={review.product.name}
                    width={logoSize}
                    height={logoSize}
                    style={{ '--product-logo-size': `${logoSize}px`}} />
                  <a href={review.product.url} className={'name block-link'}>{review.product.name}</a>
              </div>
            )
        }
    }

    const renderTitle = () => {
        if(showTitle) {
            return (
              <ConditionalWrapper
                condition={titleClamp}
                wrapper={children => <TooltipTrigger id={`tt_${review.id}`} placement={'left'} content={review.title}>{children}</TooltipTrigger>}
              >
                  {renderTitleLink()}
              </ConditionalWrapper>
            )
        }
    }

    const renderTitleLink = () => {
        let urlClasses = `title font-${titleFont} block-link ${titleClamp && 'sp-line-' + titleClamp}`;
        if(urlAsRoute) {
            return (
                <Link to={urlAsRoute} className={urlClasses}>{review.title}</Link>
            )
        } else if(review.url) {
            return (
                <a href={`${review?.url}`} className={urlClasses}>{review.title}</a>
            )
        } else {
            return (
                <div className={'d-flex justify-content-between align-items-start'}>
                    <div className={`title text-secondary font-${titleFont}  ${titleClamp && 'sp-line-' + titleClamp}`}>{review.title}</div>
                    <div className={'pt-1'}>
                        <PostingAdminMenu
                            posting={review}
                            userSignedIn={userSignedIn}
                            adminUser={adminUser}
                            viewingSelf={viewingSelf}
                            token={token}
                            type={'posting'}
                            toggleRegistration={toggleRegistration}
                            leadData={leadData}
                        />
                    </div>
                </div>
            )
        }
    }

    const renderSnippet = () => {
        if(showSnippet) {
            return (
              <div className={`snippet ${displaySnippetAsQuote && 'font-18 lh-base'}`}>
                  {review.snippet}
              </div>
            )
        }
    }

    const renderVideo = () => {
        if(review.video) {
            let { id } = getVideoId(review.video);
            return (
                <div className={'ratio ratio-16x9'}>
                    {isPlaying
                        ?
                        <iframe src={`${review.video}?autoplay=1`} allowFullScreen={true}
                                  allow={"autoplay; fullscreen; picture-in-picture"}/>
                        :
                        <div className={'gitb-dynamic-video'}
                             style={{background: `url("https://i.ytimg.com/vi/${id}/0.jpg") center center / 100% no-repeat`}}
                             onClick={() => setIsPlaying(true)}>
                            <div className={'gitb-yt-play-button'}/>
                        </div>
                    }
                </div>
            )
        }
    }

    const renderReadLink = () => {
        if (review.url || urlAsRoute) {
            const gtmDataAttributes = props.gtmEventRegion ? {
                'data-gtm-event-region': props.gtmEventRegion,
                'data-gtm-event-element': 'more-featured-interview'
            } : {}
            return (
              <div className={'text-end mt-3 mb-2'}>
                  {urlAsRoute ? <Link to={urlAsRoute} className={'text-secondary fw-bold'} {...gtmDataAttributes}>{reviewCtaText}</Link>
                    :
                    <a href={review.url} className={'text-secondary fw-bold'} {...gtmDataAttributes}>{reviewCtaText}</a>}
                  <i className={'fal fa-angle-right ps-1'}></i>
              </div>
            )
        }
    }

    const renderShortSummaryAndLanguageLink = () => {
        if(showShortSummary && review.short_summary) {
            return (
                <>
                    <div className={'d-flex align-items-center justify-content-between'}>
                        <h3 className={'py-1'}>Summary <TooltipTrigger id={`ssummary_${review.id}`} content={tooltips.review_short_summary}>
                        <i className={'fal fa-question-circle fs-6 text-blue-link'} /></TooltipTrigger></h3>
                        {renderForeignLanguageLink()}
                    </div>
                    <div className={'text-secondary font-18 content-styling'} style={{ lineHeight: 1.7 }}>{review.short_summary}</div>
                </>
            )
        } else {
            return (
                <>{renderForeignLanguageLink()}</>
            )
        }
    }

    const renderForeignLanguageLink = () => {
        if(includeForeignLanguageLink && review.foreign_language) {
            return (
                <a href={'#foreign-language'} className={'text-secondary'} onClick={(e) => onForeignLanguageClick(e)}>Read in {review.foreign_language}</a>
            )
        }
    }

    const renderProsCons = () => {
        if(showProsCons && (review.pro_point || review.con_point)) {
            return (
              <>
                  <Row className={'border-between mx-0'}>
                      {review.pro_point && <Col sm={12} md={review.con_point ? 6 : 12} className={'ps-0'}>
                          <h3 className={'py-1'}>Pros</h3>
                          {renderKeyPoint('pro')}
                      </Col>}
                      {review.con_point && <Col sm={12} md={review.pro_point ? 6 : 12}>
                          <h3 className={'py-1'}>Cons</h3>
                          {renderKeyPoint('con')}
                      </Col>}
                  </Row>
              </>
            )
        }
    }

    const renderKeyPoint = (type) => {
        return (
          <div className={'d-flex'}>
              <i className={`fas fa-${type == 'pro' ? 'check': 'times'}-circle font-18 text-${type == 'pro' ? 'green': 'reddark'} mt-1 pe-2`}></i>
              <div className={`text-secondary font-18 content-styling ${collapsedBody && 'sp-line-5'}`} style={{ lineHeight: 1.7 }}>"{type == 'pro' ? review.pro_point.body : review.con_point.body}"</div>
          </div>
        )
    }

    const renderBody = () => {
        if(showBody) {
            let body = '';
            let maxSections = 2;
            let sectionsToIterate = Object.entries(review.body);

            if (collapsedBody) {
              sectionsToIterate = sectionsToIterate.slice(0, maxSections)
            }

            for (const [key, content] of sectionsToIterate) {
                let bodyContent = content;
                if(key == 'other_advice' && includeForeignLanguageLink && review.foreign_language) {
                    // Replace the html element of Foreign Language with an h4 with an id
                    bodyContent = content.replace(
                        /<(\w+)([^>]*)>\s*Foreign Language(.*?)<\/\1>/i,
                        `<h4$2 id="foreign-language">Foreign Language$3</h4>`
                    )
                }
                if (sections[key]) body += `<h3>${sections[key].table.long_label}</h3>`;
                body += bodyContent;
            }

            let collapsibleVars = {
                lineHeight: 1.6,
                '--collapsible-show-lines': 5,
                '--collapsible-overlay-height': '60px'
            }

            return (
                <>
                    <div className={`body ${collapsedBody ? 'collapsible collapsible-by-height collapsible-hide-toggle' : 'mb-4'} text-secondary font-18 content-styling blue-link-links`} style={collapsedBody ? collapsibleVars : {}}>
                        {createElementFromHTML(body)}
                        {review.disclosure && <div className={'py-2 mt-2 font-12'}>*Disclosure: {review.disclosure}</div> }
                    </div>
                    {collapsedBody && <div className={'w-auto mb-4'}>
                        <a className={'text-secondary fw-bold fs-6'} onClick={() => onReadFullReview(review)}>
                            {reviewCtaText} ({review.word_count} words)
                        </a>
                    </div>}
                </>
            )

            // old code for collapsible content
            // let collapsibleVars = {
            //     lineHeight: 1.6,
            //     '--collapsible-more': `'Read full review (${review.word_count} words)'`,
            //     '--collapsible-less': `'Close review'`,
            //     '--collapsible-show-lines': 10,
            //     '--collapsible-overlay-height': '200px'
            // }
            // return (
            //     <div className={`body collapsible collapsible-by-height text-secondary font-18 content-styling blue-link-links mb-4`} style={collapsibleVars} onClick={async() => captureImpression(review.actions.url)} data-scroll-to-on-close={`review_${review.id}`} data-scroll-offset={75}>
            //         {createElementFromHTML(body)}
            //         {review.disclosure && <div className={'py-2 mt-2 font-12'}>*Disclosure: {review.disclosure}</div> }
            //     </div>
            // )
        }
    }

    const renderReviewActions = () => {
        if(showReviewActions) {
            return (
              <PostingActions
                posting={review}
                type={'product_review'}
                currentUser={currentUser}
                hoverBg={'light'}
                onToggleCommentForm={onToggleCommentForm}
                commentIsOpen={showCommentForm}
                token={token}
                gtmEventRegion={gtmEventRegion}
                toggleRegistration={toggleRegistration}
                leadData={leadData}
              />
            )
        }
    }

    const renderCommentForm = () => {
        if(showNewCommentForm && showCommentForm) {
            return (
              <Collapse in={showCommentForm}>
                  <div id={'toggleAnswerForm'} className={'my-2'}>
                      <CommentForm
                        currentUser={currentUser}
                        token={token}
                        parentId={review.id}
                        parentType={'review'}
                        onNewComment={onNewComment}
                        toggleRegistration={toggleRegistration}
                        leadData={leadData}
                      />
                  </div>
              </Collapse>
            )
        }
    }

    const renderComments = () => {
        if(showComments) {
            return (
              <Row>
                  <div className={'posting-comments'}>
                      { comments.map( (comment) => {
                          return (
                            <CommentCard
                              key={comment.id}
                              comment={comment}
                              postingId={review.id}
                              postingAuthorId={review.author.id}
                              currentUser={currentUser}
                              token={token}
                              onDeleteComment={onDeleteComment}
                              onRepliesUpdate={onRepliesChangeHandler}
                              cardClasses={commentCardClasses}
                              toggleRegistration={toggleRegistration}
                              leadData={leadData}
                            />
                          )})
                      }
                      {showMoreComments && <div className={'fs-6'}>
                          <i className={'fal fa-comment-alt text-primary me-2'}></i>
                          <a
                            onClick={() => onListComments()}
                            className={'text-secondary'}>
                              See all {commentCount} comments
                          </a>
                      </div>}
                  </div>
              </Row>
            )
        }
    }

    const renderProductVersion = () => {
        if(showProductVersion && review.product_version) {
            return (
                <>
                    <div className={'break'}></div>
                    <div className={'gray-600 font-14 pt-2'}>Product version discussed: {review.product_version}</div>
                </>
            )
        }
    }

    const renderRatingAndSentiment = () => {
        if (showRating || showSentimentScore) {
            return (
                <ul className={'list-inline'}>
                    {showRating && review.rating > 0 &&
                        <li className={'list-inline-item'}>{renderRatingStars(review.rating)}</li>}
                    {showSentimentScore && valueExists(review.sentiment) && <li className={'list-inline-item'}>{renderSentimentScore()}</li>}
                </ul>
            )
        }
    }

    const renderSentimentScore = () => {
        return (
            <>
                <span className={'content-styling gray-600'}>Interview sentiment</span>
                <span className={'fw-bold text-secondary px-1'}>{review.sentiment}</span>
                <TooltipTrigger id={`ss-tt_${review.id}`} placement={'bottom'} content={SENTIMENT_SCORE_TOOLTIP}>
                    <i className={'fal fa-question-circle fs-6 text-blue-link'} /></TooltipTrigger>
            </>
        )
    }

    const renderAudio = (review) => {
        if(showAudio && !hideAudio && review.actions?.audio?.url) {
            // don't put any styling on the audio for firefox and safari
            let displayName = !review.author.is_anonymous ? review.author.first_name : null;
            return (
                <div className={'flex-grow-1 '}>
                    <div className={'text-medium pb-1'}>
                        Highlights from {displayName ? `${displayName}'s` : 'the'} interview
                        <span className={'px-1'}>-</span>
                        <i className={'fal fa-clock-three font-12'}></i> {Math.max(review.actions.audio.duration,1)} minute listen
                    </div>
                    <div
                        className={`${!isSafari && !isFirefox && 'border rounded-3 bg-medium'} position-relative`}
                        style={{
                            cursor: 'pointer',
                            maxWidth: '500px',
                            height: (!isSafari && !isFirefox ? '56px' : 'auto')
                        }}
                        data-gtm-event-region={props.gtmEventRegion} data-gtm-event-element='listen'
                        onClick={() => onAudioListen()}>
                        <div className={'position-absolute w-100 h-100'} style={{zIndex: 99}}/>
                        <audio controls className={`${!isSafari && !isFirefox && 'transparent-dark'} w-100`}
                               title={'Listen to the interview'} controlsList="nodownload">
                            <source src={review.actions.audio.url} type={review.actions.audio.type}/>
                        </audio>
                    </div>
                </div>
            )
        }
    }

    return (
        <div className={`${displayInCard ? 'card' : 'px-0'} review-card ${!review?.url && 'shadow-none'}`}
             id={`review_${review.id}`} style={{scrollMarginTop: '160px'}}>
            {renderProduct()}
            {collapsedBody && showUser &&
                <UserCard user={review.author} avatarSize={avatarSize} showBadges={showUserBadges}
                          cardClasses={authorCardClasses} productUser={showProductUserVerification}/>}
            <Row className={'mx-0'}>
                <Col lg={12} xl={(showBody && collapsedBody) && showUser ? {span: 11, offset: 1} : 12}
                     className={'px-0 px-lg-1'}>
                    <div className={'content'}>
                        {!collapsedBody && renderTitle()}
                        {!collapsedBody && showUser && <UserCard user={review.author} avatarSize={avatarSize} showBadges={showUserBadges} cardClasses={authorCardClasses} productUser={showProductUserVerification} />}
                      <div className={`rating flex-wrap justify-content-between align-items-start column-gap-3 ${showAudio && 'mb-0'}`} style={showAudio && review.actions?.audio?.url ? { height: '83px'} : {}}>
                          {renderRatingAndSentiment()}
                          {renderAudio(review)}
                          {showDate &&  <div className={`date content-styling ${!showRating && !showAudio && 'ms-0'}`}>{formatDate(review.published_date)}</div> }
                      </div>
                      {renderProductVersion()}
                      {collapsedBody && renderTitle()}
                      {renderSnippet()}
                      {renderReadLink()}
                      {renderShortSummaryAndLanguageLink()}
                      {renderVideo()}
                      {renderProsCons()}
                      {renderBody()}
                      {renderReviewActions()}
                      {renderCommentForm()}
                      {renderComments()}

                  </div>
              </Col>
          </Row>
      </div>
    )

}
